import { portalApi } from "@services/api/portalApi";
import { apiC } from "@services/api/apiC";

export type Group = {
    info: {
        identifier: number;
        name: string;
        type: GroupType;
        code: string;
    };
    data: {
        attendance: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        punctuality: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        retention: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        studentCount: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        gcsePoints: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        predictedGrade: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        };
        sr1Grade: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr1Total: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr2Grade: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr2Total: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr3Grade: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr3Total: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr4Grade: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        sr4Total: {
            lowerOr1YearALevel: string,
            lowerOr1YearBTEC: string,
            upper2YearALevel: string,
            upper2YearBTEC: string,
            overallALevel: string,
            overallBTEC: string
        },
        highGrades: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        valueAdded: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        send: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        accessArrangements: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        leaf: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
        safeguarding: {
            lowerOr1Year: number;
            upper2Year: number;
            overall: number;
        };
    }
}

export type GroupFetchMode = 'my' | 'all' | 'id'
export type GroupType = 'faculty' | 'department' | 'programme' | 'course' | 'class' | 'myClass';

export type GroupFetchParams = {
    mode: GroupFetchMode;
    type: GroupType;
    identifier?: string;
}

export async function fetchGroups(params: GroupFetchParams): Promise<Group[]> {
    try {
        const response = await apiC.get(`/TeacherPortalGroupList`, {
            params: {
                type: params.type,
                acadYearFull: 2026,
                identifier: params.identifier, //params.mode === 'id' ? params.identifier : undefined,
            }
        });
        return response.data
    } catch (error) {
        console.error("Error fetching groups:", error);
        throw error;
    }
}

export type Faculty = {
    facultyId: number;
    facultyName: string;
    dofInitials: string;
}

export type Department = {
    departmentId: number;
    departmentName: string;
    hodInitials: string;
} & Faculty

export type Subject = {
    subjectId: number;
    subjectName: string;
    programmeName: string;
} & Department

export type Course = {
    courseId: number;
    courseCode: string;
    courseType: string;
    courseLeaderInitials: string | null;
} & Subject

export type Class = {
    classId: number;
    classCode: string;
    teacherInitials: string | null;
} & Course

export async function fetchMyClasses(): Promise<Class[]> {
    try {
        const response = await portalApi.get('/academic-structure/me/classes')
        return response.data
    } catch (error) {
        console.error("Error fetching my classes:", error); 
        throw error; 
    } 
}

export type GroupInfoFetchParams = {
    mode: 'my' | 'id' | 'all';
    id?: string;
    usernameOverride?: string;
}

export async function fetchFaculty(params: GroupInfoFetchParams): Promise<Faculty> {
    try {
        const response = await portalApi.get(`/academic-structure/faculty`, {
            params: {
                mode: params.mode,
                id: params.id,
            }
        })
        return response.data
    } catch (error) {
        console.error(`Error fetching faculty with ID ${params.id}:`, error); 
        throw error; 
    }
}

export async function fetchDepartment(params: GroupInfoFetchParams): Promise<Department> {
    try {
        const response = await portalApi.get(`/academic-structure/department`, {
            params: {
                mode: params.mode,
                id: params.id
            }
        })
        return response.data
    } catch (error) {
        console.error(`Error fetching department with ID ${params.id}:`, error); 
        throw error; 
    }
}

export async function fetchSubject(params: GroupInfoFetchParams): Promise<Subject> {
    try {
        const response = await portalApi.get(`/academic-structure/subject`, {
            params: {
                mode: params.mode,
                id: params.id
            }
        })
        return response.data
    } catch (error) {
        console.error(`Error fetching subject with ID ${params.id}:`, error); 
        throw error; 
    }
}

export async function fetchCourse(params: GroupInfoFetchParams): Promise<Course> {
    try {
        const response = await portalApi.get(`/academic-structure/course`, {
            params: {
                mode: params.mode,
                id: params.id
            }
        })
        return response.data
    } catch (error) {
        console.error(`Error fetching course with ID ${params.id}:`, error); 
        throw error; 
    }
}

export async function fetchClass(params: GroupInfoFetchParams): Promise<Class> {
    try {
        const response = await portalApi.get(`/academic-structure/class`, {
            params: {
                mode: params.mode,
                id: params.id
            }
        })
        return response.data
    } catch (error) {
        console.error(`Error fetching class with ID ${params.id}:`, error); 
        throw error; 
    }
}
